import { queryOptions, type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createAreaTagSet,
  deleteAreaTagSet,
  getAreaTagSets,
  getProjectLayouts,
  getProjectAreaTagLocationOptions,
  updateAreaTagSet,
} from './api';
import type { AreaTagSet, CreateAreaTagSetData, UpdateAreaTagSetData } from './types';

export const areaTagSetKeys = {
  all: ['area-tag-sets'] as const,
  // Retain the existing layout key so host consumers share their cached data.
  projectLayouts: (projectId: number) => ['punch-lists', 'layouts', projectId] as const,
  list: (projectId: string, layoutId: string) =>
    [...areaTagSetKeys.all, projectId, layoutId] as const,
  projectLocationOptions: (projectId: number) =>
    [...areaTagSetKeys.all, 'project-location-options', projectId] as const,
};

export const projectLayoutsQueryOptions = (projectId: number) => queryOptions({
  queryKey: areaTagSetKeys.projectLayouts(projectId),
  queryFn: () => getProjectLayouts(projectId),
  staleTime: 10 * 60_000,
});

export const projectLocationQueryOptions = (projectId: number, client: QueryClient) => queryOptions({
  queryKey: areaTagSetKeys.projectLocationOptions(projectId),
  queryFn: async () => {
    const layouts = await client.fetchQuery(projectLayoutsQueryOptions(projectId));
    return getProjectAreaTagLocationOptions(projectId, layouts);
  },
  staleTime: 5 * 60_000,
});

export const useProjectAreaTagLocationOptions = (projectId: number | undefined) => {
  const client = useQueryClient();
  return useQuery({
    ...projectLocationQueryOptions(projectId!, client),
    enabled: Boolean(projectId),
  });
};

export const useAreaTagSets = (layoutId: string, projectId: string) =>
  useQuery({
    queryKey: areaTagSetKeys.list(projectId, layoutId),
    queryFn: () => getAreaTagSets(layoutId, projectId),
    enabled: Boolean(layoutId && projectId),
    staleTime: 30_000,
  });

export const useCreateAreaTagSet = (layoutId: string, projectId: string) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagSetKeys.list(projectId, layoutId);

  return useMutation<AreaTagSet, Error, CreateAreaTagSetData, { previous?: AreaTagSet[] }>({
    mutationFn: (data) => createAreaTagSet(data, projectId),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AreaTagSet[]>(queryKey);
      const now = new Date().toISOString();
      queryClient.setQueryData<AreaTagSet[]>(queryKey, (current = []) => [
        ...current,
        {
          id: -Date.now(),
          name: data.name,
          project: Number(projectId),
          layout: data.layout,
          area_tag_ids: data.area_tag_ids,
          created_by: null,
          created_at: now,
          updated_at: now,
        },
      ]);
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSuccess: (created) => {
      queryClient.setQueryData<AreaTagSet[]>(queryKey, (current = []) => [
        ...current.filter((item) => item.id >= 0),
        created,
      ]);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: areaTagSetKeys.projectLocationOptions(Number(projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: ['punch-lists'] });
      void queryClient.invalidateQueries({ queryKey: ['observations'] });
    },
  });
};

export const useUpdateAreaTagSet = (layoutId: string, projectId: string) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagSetKeys.list(projectId, layoutId);

  return useMutation<
    AreaTagSet,
    Error,
    { id: number; data: UpdateAreaTagSetData },
    { previous?: AreaTagSet[] }
  >({
    mutationFn: ({ id, data }) => updateAreaTagSet(id, data, layoutId, projectId),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AreaTagSet[]>(queryKey);
      queryClient.setQueryData<AreaTagSet[]>(queryKey, (current = []) =>
        current.map((item) => (item.id === id ? { ...item, ...data } : item))
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<AreaTagSet[]>(queryKey, (current = []) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: areaTagSetKeys.projectLocationOptions(Number(projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: ['punch-lists'] });
      void queryClient.invalidateQueries({ queryKey: ['observations'] });
    },
  });
};

export const useDeleteAreaTagSet = (layoutId: string, projectId: string) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagSetKeys.list(projectId, layoutId);

  return useMutation<void, Error, number, { previous?: AreaTagSet[] }>({
    mutationFn: (id) => deleteAreaTagSet(id, layoutId, projectId),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AreaTagSet[]>(queryKey);
      queryClient.setQueryData<AreaTagSet[]>(queryKey, (current = []) =>
        current.filter((item) => item.id !== id)
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: areaTagSetKeys.projectLocationOptions(Number(projectId)),
      });
      void queryClient.invalidateQueries({ queryKey: ['punch-lists'] });
      void queryClient.invalidateQueries({ queryKey: ['observations'] });
    },
  });
};
